/**
 * Unified Catalog (Push #3.2.5) — single endpoint across all three pillars.
 *
 * Push 4-1d-m (2026-07-06): per-kind detail endpoints added
 * (fetchCatalogConnectorDetail / fetchCatalogModelDetail). Category A
 * cross-schema discipline: /market/{kind}/:short_id sources workspace_directory
 * TEMPLATE tables (shared catalog), never workspace_selfservice instances.
 */

import { get } from '../_shared.ts';
import type { CatalogDetail } from './connectors.ts';

// ---- Unified Catalog (Push #3.2.5) ----
// Single endpoint returns all three pillars' platform-tier templates with
// status so the /catalog 3-tab UI can render Install/Coming-soon decisions
// deterministically. Reachable by any authed user (admin install action
// itself still routes through admin-gated POST endpoints).

export type CatalogStatus = 'active' | 'coming_soon';

export interface UnifiedConnectorTpl {
  id: string;
  /** Phase 2 Block 2A — connector_template.short_id (cnt-prefix). Drives
   *  /market/:short_id navigation from the Market tile (URL identifier
   *  discipline per PATTERNS.md — short_id text, not UUID, not slug). */
  short_id: string;
  name: string;
  display_name: string;
  description: string | null;
  connector_mode: string;
  logo_url: string | null;
  status: CatalogStatus;
  /** Per /api/catalog/all 2026-06-10: true if the calling workspace already
   * has a non-deleted instance of this template. UI filters these out of
   * the Available section. */
  is_installed_in_workspace: boolean;
  /** Caller's own assigned mcp__connector_instance.short_id (cni-prefix) or
   *  null when the caller has no active assignment for this template. Drives
   *  the whole-tile "Open" click on installed connector tiles → routes to
   *  /connectors/<instance_short_id> instead of the template slug. */
  instance_short_id: string | null;
  /** Global upvote count across all workspaces. Read directly from
   * mcp__connector_template.upvote_count per Migration H (2026-06-24).
   * Shown on Coming Soon cards next to the Upvote button. */
  upvote_count: number;
}
export interface UnifiedModelTpl {
  id: string;
  /** Phase 2 Block 2A — llm__model_template.short_id (mdt-prefix). */
  short_id: string;
  model_id: string;
  display_name: string;
  description: string | null;
  provider_name: string;
  provider_display_name: string;
  context_window: number | null;
  security_tier: string;
  /** Push #75 — D-150 multi-axis tags surfaced in the catalog. Vocabulary:
   *  reasoning · fast · image_generation · code · cost_optimized · regulated.
   *  Null when no tags are set on the template; rendered as small pills on
   *  the ModelTile in the user-mode catalog. */
  recommended_for: string[] | null;
  status: CatalogStatus;
  is_installed_in_workspace: boolean;
  /** Caller's own assigned llm__model_instance.short_id (lmi-prefix) or null
   *  when the caller has no active assignment for this template. Drives
   *  the whole-tile "Open" click on installed model tiles → routes to
   *  /models/<instance_short_id> instead of the template model_id slug. */
  instance_short_id: string | null;
  upvote_count: number;
}
export interface UnifiedKnowledgeTpl {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  category: string | null;
  status: CatalogStatus;
  is_installed_in_workspace: boolean;
  upvote_count: number;
}

export function fetchUnifiedCatalog(): Promise<{
  connectors: UnifiedConnectorTpl[];
  models: UnifiedModelTpl[];
  knowledge: UnifiedKnowledgeTpl[];
}> {
  return get('/api/catalog/all');
}

// ---------------------------------------------------------------------
// Per-kind market detail (Push 4-1d-m, 2026-07-06)
// ---------------------------------------------------------------------
//
// URL discipline lock: /market/{kind}/:short_id (user) and
// /admin/market/{kind}/:short_id (admin). Kinds: 'connectors' + 'models'.
// The kind segment in the URL communicates the category so the SPA route
// can bind to a kind-specific detail component. The endpoints below are
// the read side; install actions still route through kind-specific admin
// POSTs (installConnector / cloneModelTemplate).

/**
 * Model-template catalog detail. Sources workspace_directory.llm__model_template
 * (Pattern 5 catalog). is_installed / is_mine / instance_short_id fields hit
 * workspace_selfservice under withScope on the server. Caller passes
 * llm__model_template.short_id (mdt-prefix).
 */
export interface CatalogModelDetail {
  template: {
    id: string;
    /** llm__model_template.short_id (mdt-prefix) */
    short_id: string;
    /** Vendor-side model slug (e.g. "claude-haiku-4-5") */
    model_id: string;
    display_name: string;
    description: string | null;
    /** 'chat' | 'embed' etc — llm__model_template.kind */
    kind: string;
    /** Max input tokens */
    context_window: number | null;
    /** Numeric → text via ::text; parse client-side if needed for math. */
    price_per_input_token: string | null;
    price_per_output_token: string | null;
    /** Provider-agnostic capabilities from D-150 vocabulary (vision, function_calling, etc.) */
    capabilities: string[];
    security_tier: string;
    recommended_for: string[];
    /** 'active' | 'coming_soon' | 'archived' — archived filtered out server-side */
    status: string;
    upvote_count: number;
    /** llm__provider_template.name (slug — 'anthropic', 'openai', ...) */
    provider_name: string;
    provider_display_name: string;
    /** llm__provider_template.short_id — for provider chip deep-link if surfaced */
    provider_template_short_id: string;
    /** llm__provider_template.trust_model — 'first_party' | 'byo' | etc */
    trust_model: string | null;
    /** True if any llm__model_instance exists for this template in the caller's account */
    is_installed: boolean;
    /** True if the caller has an active assignment for an instance of this template */
    is_mine: boolean;
    /** llm__model_instance.short_id (lmi-prefix) for the caller's assigned instance, or null */
    instance_short_id: string | null;
  };
}

export function fetchCatalogConnectorDetail(short_id: string): Promise<CatalogDetail> {
  return get<CatalogDetail>(`/api/catalog/connectors/${encodeURIComponent(short_id)}`);
}

export function fetchCatalogModelDetail(short_id: string): Promise<CatalogModelDetail> {
  return get<CatalogModelDetail>(`/api/catalog/models/${encodeURIComponent(short_id)}`);
}
